import * as React from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Subject } from 'rxjs';
import { ImportType } from '../interfaces/import-type';
import { takeUntil } from 'rxjs/operators';
import { uploadImportEnedis } from '../webapis/import-types.webapi';

interface Props {
    open: boolean;
    importType: ImportType;
    onClose: (event: any) => void;
}

interface State {
    isFileImporting: boolean;
    scheduledAt: string;              // valeur brute du champ datetime-local, '' = lancement immédiat
    clearBeforeImport: boolean;
    scheduledAtError: string | null;
    resumeFromUrl: string;            // url « Point de reprise » de la console Hangfire, '' = import depuis le début
}

export default class ImportCsvFileDialog extends React.Component<Props, State> {
    private destroyed$ = new Subject<void>();

    constructor(props: Props) {
        super(props);

        this.state = {
            isFileImporting: false,
            scheduledAt: '',
            clearBeforeImport: false,
            scheduledAtError: null,
            resumeFromUrl: '',
        } as State;
    }

    public render() {
        const { open, onClose, importType } = this.props;
        const { isFileImporting, scheduledAt, clearBeforeImport, scheduledAtError, resumeFromUrl } = this.state;
        return (
            <div>
                <Dialog open={open} onClose={onClose} aria-labelledby='form-dialog-title'>
                    <DialogTitle id='form-dialog-title'>Notification</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {isFileImporting
                                ? (scheduledAt
                                    ? 'L\'import est planifié le ' + new Date(scheduledAt).toLocaleString('fr-FR') + '. Un mail vous sera envoyé à la fin de l\'import.'
                                    : 'L\'import à été lancé. Cette opération peut prendre un peu de temps. Un mail vous sera envoyé à la fin de l\'import.')
                                : 'Pour sauvegarder les données, cliquer sur le bouton « IMPORTER ».'}
                        </DialogContentText>
                        {isFileImporting
                            ? '' //<CircularProgress />
                            : <>
                                <DialogContentText>{importType.sourceUri}</DialogContentText>
                                <TextField
                                    label='Planifier le lancement (optionnel)'
                                    type='datetime-local'
                                    value={scheduledAt}
                                    onChange={this.handleScheduledAtChange}
                                    error={!!scheduledAtError}
                                    helperText={scheduledAtError || 'Laisser vide pour lancer l\'import immédiatement.'}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: this.toDatetimeLocalString(new Date()) }}
                                    fullWidth
                                    margin='normal' />
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={clearBeforeImport}
                                        onChange={this.handleClearBeforeImportChange}
                                        disabled={!!resumeFromUrl}
                                        color='primary' />}
                                    label={'Supprimer les lignes existantes avant l\'import'} />
                                <TextField
                                    label='URL de reprise (optionnel)'
                                    value={resumeFromUrl}
                                    onChange={this.handleResumeFromUrlChange}
                                    disabled={clearBeforeImport}
                                    helperText={'Coller l\'url « Point de reprise » de la console Hangfire pour reprendre un import interrompu sans retraiter les pages déjà insérées. Incompatible avec la suppression des lignes existantes.'}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    margin='normal' />
                            </>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} color='primary'>
                            {isFileImporting
                                ? 'Quitter'
                                : 'Annuler'}
                        </Button>
                        {isFileImporting
                            ? ''
                            : <Button onClick={this.handleFileSubmit} color='primary' disabled={!!scheduledAtError}>
                                {scheduledAt ? 'Planifier' : 'Importer'}
                            </Button>}
                    </DialogActions>
                </Dialog>
            </div>
        );
    }

    public componentDidMount() {
    }

    public componentWillUnmount() {
        this.destroyed$.next();

        // remove eventListener in case of changing pages
        // (might be another "+" button in other page...)
        // window.removeEventListener('load', () => { });
    }

    // Format 'YYYY-MM-DDTHH:mm' local attendu par l'attribut min d'un input datetime-local
    private toDatetimeLocalString = (date: Date): string => {
        const pad = (n: number) => (n < 10 ? '0' + n : String(n));
        return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
            + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    }

    private handleScheduledAtChange = (event: any) => {
        this.setState({ scheduledAt: event.target.value, scheduledAtError: null });
    }

    private handleClearBeforeImportChange = (event: any) => {
        this.setState({ clearBeforeImport: event.target.checked });
    }

    private handleResumeFromUrlChange = (event: any) => {
        this.setState({ resumeFromUrl: event.target.value });
    }

    private handleFileSubmit = (event: any) => {
        event.preventDefault();

        const { scheduledAt, clearBeforeImport, resumeFromUrl } = this.state;
        const { importType } = this.props;

        let scheduledAtIso: string | undefined;
        if (scheduledAt) {
            const scheduledDate = new Date(scheduledAt);
            if (scheduledDate.getTime() <= Date.now()) {
                this.setState({ scheduledAtError: 'La date de planification doit être dans le futur.' });
                return;
            }
            // toISOString => instant UTC non ambigu pour le binding DateTimeOffset côté back
            scheduledAtIso = scheduledDate.toISOString();
        }

        // trim : les urls collées depuis la console Hangfire embarquent souvent des espaces
        uploadImportEnedis(importType.id, scheduledAtIso, clearBeforeImport, resumeFromUrl.trim() || undefined);
        this.setState({ isFileImporting: true });
    }
}